import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Save, AlertCircle, Upload, FileText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCreateRubricTemplate, RubricCriterion, RubricKnowledgeSource } from '@/hooks/useRubricTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { uploadAndParseDocument, deleteDocumentFromStorage } from '@/lib/documentParser';
import { AgentUserAssignment } from '@/components/admin/AgentUserAssignment';
import { MASTER_TENANT_ID } from '@/types/tenant';
import { toast } from 'sonner';
import { fetchTenantScopedCompanies, resolveTenantId } from '@/lib/tenantScope';
import {
  clearTemplateDraft,
  createTemplateDraftKey,
  loadTemplateDraft,
  saveTemplateDraft,
} from '@/lib/templateDraftStorage';

const TEMPLATE_DRAFT_MAX_AGE_DAYS = 30;

export default function CreateRubricTemplate() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const currentTenantId = resolveTenantId(tenant, user?.tenant_id);
  const createTemplate = useCreateRubricTemplate();
  const knowledgeFileInputRef = useRef<HTMLInputElement>(null);
  const draftReadyRef = useRef(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'patient' | 'customer' | 'coach' | 'examiner'>('patient');
  const [assignTo, setAssignTo] = useState<'all' | 'specific_companies' | 'specific_users'>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [assignToAllUsers, setAssignToAllUsers] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [knowledgeSources, setKnowledgeSources] = useState<RubricKnowledgeSource[]>([]);
  const [uploadingKnowledge, setUploadingKnowledge] = useState(false);
  const [criteria, setCriteria] = useState<RubricCriterion[]>([
    { criterion: '', weight: 25, description: '' },
    { criterion: '', weight: 25, description: '' },
    { criterion: '', weight: 25, description: '' },
    { criterion: '', weight: 25, description: '' }
  ]);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', currentTenantId, isMasterTenant],
    queryFn: () => fetchTenantScopedCompanies({
      tenantId: currentTenantId,
      isMasterTenant,
      select: 'id, name',
    }),
    enabled: hasRole('admin') && (isMasterTenant || !!currentTenantId)
  });

  const draftKey = user?.id && currentTenantId
    ? createTemplateDraftKey({ area: 'rubric-template', tenantId: currentTenantId, userId: user.id })
    : null;

  const draftHasContent = () => (
    name.trim() ||
    description.trim() ||
    criteria.some(criterion => criterion.criterion.trim() || criterion.description.trim()) ||
    knowledgeSources.length > 0
  );

  const persistDraft = (showToast = false) => {
    if (!draftKey || !draftHasContent()) return false;

    const saved = saveTemplateDraft(window.localStorage, draftKey, {
      name,
      description,
      category,
      assignTo,
      selectedCompanyId,
      assignToAllUsers,
      selectedUserIds,
      knowledgeSources,
      criteria,
    });

    if (showToast) {
      if (saved) {
        toast.success('Draft saved');
      } else {
        toast.error('Unable to save draft in this browser');
      }
    }

    return Boolean(saved);
  };

  useEffect(() => {
    if (!draftKey || draftReadyRef.current) return;

    const draft = loadTemplateDraft<{
      name?: string;
      description?: string;
      category?: typeof category;
      assignTo?: typeof assignTo;
      selectedCompanyId?: string | null;
      assignToAllUsers?: boolean;
      selectedUserIds?: string[];
      knowledgeSources?: RubricKnowledgeSource[];
      criteria?: RubricCriterion[];
    }>(window.localStorage, draftKey, { maxAgeDays: TEMPLATE_DRAFT_MAX_AGE_DAYS });

    if (draft) {
      setName(draft.payload.name || '');
      setDescription(draft.payload.description || '');
      if (draft.payload.category) setCategory(draft.payload.category);
      if (draft.payload.assignTo) setAssignTo(draft.payload.assignTo);
      setSelectedCompanyId(draft.payload.selectedCompanyId || null);
      setAssignToAllUsers(draft.payload.assignToAllUsers ?? true);
      setSelectedUserIds(draft.payload.selectedUserIds || []);
      setKnowledgeSources(draft.payload.knowledgeSources || []);
      if (draft.payload.criteria?.length) setCriteria(draft.payload.criteria);
      toast.info('Restored your saved rubric template draft');
    }

    draftReadyRef.current = true;
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReadyRef.current || !draftHasContent()) return;

    const timeout = window.setTimeout(() => {
      persistDraft(false);
    }, 900);

    return () => window.clearTimeout(timeout);
  }, [
    draftKey,
    name,
    description,
    category,
    assignTo,
    selectedCompanyId,
    assignToAllUsers,
    selectedUserIds,
    knowledgeSources,
    criteria,
  ]);

  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);

  const addCriterion = () => {
    setCriteria([...criteria, { criterion: '', weight: 0, description: '' }]);
  };

  const removeCriterion = (index: number) => {
    if (criteria.length > 2) {
      setCriteria(criteria.filter((_, i) => i !== index));
    }
  };

  const updateCriterion = (index: number, field: keyof RubricCriterion, value: string | number) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: field === 'weight' ? Number(value) || 0 : value };
    setCriteria(updated);
  };

  const handleKnowledgeFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (knowledgeSources.length + files.length > 5) {
      alert('Maximum 5 rubric knowledge files allowed');
      return;
    }

    setUploadingKnowledge(true);
    try {
      const uploaded: RubricKnowledgeSource[] = [];
      for (const file of files) {
        const result = await uploadAndParseDocument(file, 'knowledge-sources', 'rubric-templates');
        if (result.success && result.url) {
          uploaded.push({
            name: result.name,
            url: result.url,
            extracted_text: result.extracted_text,
            word_count: result.word_count,
          });
        }
      }
      if (uploaded.length > 0) {
        setKnowledgeSources(prev => [...prev, ...uploaded]);
      }
    } finally {
      setUploadingKnowledge(false);
      if (knowledgeFileInputRef.current) knowledgeFileInputRef.current.value = '';
    }
  };

  const removeKnowledgeSource = async (index: number) => {
    const fileToRemove = knowledgeSources[index];
    if (fileToRemove?.url) {
      await deleteDocumentFromStorage(fileToRemove.url, 'knowledge-sources');
    }
    setKnowledgeSources(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;
    if (criteria.some(c => !c.criterion.trim())) return;
    if (assignTo !== 'all' && !selectedCompanyId) {
      toast.error(`Select a ${hasEnterpriseHierarchy ? 'branch' : 'company'} for restricted template access`);
      return;
    }
    if (assignTo === 'specific_users' && !assignToAllUsers && selectedUserIds.length === 0) {
      toast.error('Select at least one user, or assign the template to all users in the company');
      return;
    }
    
    // Company admins: auto-assign to their company, no global access
    // Super admins: can choose all or specific company
    const isAdmin = hasRole('admin');
    const finalCompanyId = isAdmin 
      ? (assignTo !== 'all' ? selectedCompanyId : null)
      : user?.company_id || null;
    const finalAssignTo = isAdmin
      ? (assignTo === 'specific_users' && assignToAllUsers ? 'specific_companies' : assignTo)
      : 'specific_companies'; // Company admins always company-scoped
    const finalAssignedCompanies = finalCompanyId ? [finalCompanyId] : [];
    const finalAssignedUsers = finalAssignTo === 'specific_users' ? selectedUserIds : [];
    const finalTenantId = currentTenantId || MASTER_TENANT_ID;
    
    await createTemplate.mutateAsync({
      name,
      description,
      category,
      criteria,
      knowledge_sources: knowledgeSources,
      show_to_users: false,
      is_default: false,
      is_active: true,
      assign_to: finalAssignTo,
      assigned_companies: finalAssignedCompanies,
      assigned_users: finalAssignedUsers,
      company_id: finalCompanyId,
      tenant_id: finalTenantId,
      is_system: isMasterTenant && finalAssignTo === 'all',
      created_by: user?.id || null
    });

    if (draftKey) clearTemplateDraft(window.localStorage, draftKey);
    
    navigate('/admin/rubric-templates');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/rubric-templates')} className="mb-4 -ml-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Rubric Templates
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Create Rubric Template</h1>
          <p className="text-muted-foreground mt-1">Define evaluation criteria for agent assessments</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
              <CardDescription>Basic information about this rubric template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Medical Consultation Rubric"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe when to use this rubric..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Agent Category *</Label>
                <Select value={category} onValueChange={(v: any) => setCategory(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">🏥 Patient</SelectItem>
                    <SelectItem value="customer">💼 Customer</SelectItem>
                    <SelectItem value="coach">🎓 Coach</SelectItem>
                    <SelectItem value="examiner">👨‍⚕️ Examiner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Evaluation Criteria
                <Badge variant={totalWeight === 100 ? 'default' : 'destructive'}>
                  Total: {totalWeight}%
                </Badge>
              </CardTitle>
              <CardDescription>Define scoring criteria (weights must total 100%)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {totalWeight !== 100 && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>Criteria weights must total 100%. Current: {totalWeight}%</AlertDescription>
                </Alert>
              )}

              {criteria.map((criterion, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Criterion {index + 1}</span>
                    {criteria.length > 2 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCriterion(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <Label>Name *</Label>
                      <Input
                        value={criterion.criterion}
                        onChange={(e) => updateCriterion(index, 'criterion', e.target.value)}
                        placeholder="e.g., Communication Skills"
                        required
                      />
                    </div>
                    <div>
                      <Label>Weight (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={criterion.weight}
                        onChange={(e) => updateCriterion(index, 'weight', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={criterion.description}
                      onChange={(e) => updateCriterion(index, 'description', e.target.value)}
                      placeholder="How this criterion is evaluated..."
                    />
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addCriterion} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Add Criterion
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Evaluation Knowledge Base</CardTitle>
              <CardDescription>Optional supporting files the evaluator should use with this rubric template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => knowledgeFileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploadingKnowledge ? 'Uploading and reading files...' : 'Click to upload rubric knowledge files'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Up to 5 files: PDF, TXT, DOC, DOCX</p>
                <input
                  ref={knowledgeFileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.doc,.docx"
                  onChange={handleKnowledgeFileUpload}
                  className="hidden"
                />
              </div>

              {knowledgeSources.length > 0 && (
                <div className="space-y-2">
                  {knowledgeSources.map((source, index) => (
                    <div key={`${source.url}-${index}`} className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="truncate text-sm font-medium">{source.name}</span>
                        {source.word_count ? (
                          <Badge variant="secondary" className="text-[10px]">{source.word_count} words</Badge>
                        ) : null}
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeKnowledgeSource(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Visibility & Access</CardTitle>
              <CardDescription>Control who can see and use this template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasRole('admin') && (
                <>
                  <div>
                    <Label>Access Control</Label>
                    <Select value={assignTo} onValueChange={(v: any) => setAssignTo(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">🌐 All Users</SelectItem>
                        <SelectItem value="specific_companies">🏢 Specific {hasEnterpriseHierarchy ? 'Branches' : 'Companies'}</SelectItem>
                        <SelectItem value="specific_users">👥 Specific Users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {assignTo !== 'all' && (
                    <div>
                      <Label>{hasEnterpriseHierarchy ? 'Branch' : 'Company'}</Label>
                      <Select value={selectedCompanyId || ''} onValueChange={setSelectedCompanyId}>
                        <SelectTrigger>
                          <SelectValue placeholder={`Select a ${hasEnterpriseHierarchy ? 'branch' : 'company'}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {assignTo === 'specific_users' && (
                    <AgentUserAssignment
                      companyId={selectedCompanyId}
                      assignToAll={assignToAllUsers}
                      onAssignToAllChange={setAssignToAllUsers}
                      selectedUserIds={selectedUserIds}
                      onSelectedUsersChange={setSelectedUserIds}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/rubric-templates')} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => persistDraft(true)}
              className="flex-1"
              disabled={!draftKey || !draftHasContent()}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button 
              type="submit" 
              className="flex-1" 
              disabled={createTemplate.isPending || totalWeight !== 100 || !name.trim()}
            >
              <Save className="w-4 h-4 mr-2" />
              {createTemplate.isPending ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
